"use client";

import { useCallback, useEffect, useMemo, useRef } from "react";
import { Any, MetricsEvents, PAGE_VIEW_EVENTS, SendEventDtoType } from "@cede/types";
import type { MetricsFlushDeps, MetricsIdentityStamp } from "@cede/utils";
import {
  ACTIONABLE_UNDELIVERED_REASONS,
  API_ENDPOINT,
  attachMetricsUnloadFlush,
  flushMetricsQueue,
  isPermanentSendFailure,
} from "@cede/utils";
import { useApi } from "@/hooks/core/useApi";
import { getPreviousPage } from "@/hooks/data/previousPageTracker";
import { useHubUser } from "@/hooks/user/useHubUser";
import { mixpanelClient } from "@/lib/mixpanel";
import { clearQueuedMetricsEvents, metricsEventsQueue } from "@/lib/mixpanel/metricsEventsQueue";
import { buildQueuedEvent } from "@/lib/mixpanel/queuedEvent";
import { analyticsAllowed, useAnalyticsAllowed } from "@/store/consentStore";
import * as Sentry from "@sentry/nextjs";
import { useMutation } from "@tanstack/react-query";

/**
 * A failed flush only restores the queue in memory. The subscriber re-drives delivery
 * on the next tracked event, but an idle tab would otherwise wait indefinitely.
 */
const FLUSH_RETRY_COUNT = 10;
const FLUSH_RETRY_DELAY_MS = 1_000;

/**
 * Last chance for unload, where retaining a batch loses it. Anonymous on purpose: a
 * stored `$user_id` names whoever identified on this device last, and `reset()` runs
 * only in `logout()`, not on token expiry or an external wallet-account switch, so it
 * can belong to a previous user. Mixpanel merges the device id onto the right user
 * once a later identified event carries the same `$device_id`; it cannot unwrite a
 * batch filed under the wrong one.
 */
const resolveAnonymousIdentity = (): MetricsIdentityStamp | null => {
  const deviceId = mixpanelClient.getDistinctId();
  if (!deviceId) return null;

  return {
    key: `anonymous:${deviceId}`,
    stamp: (event) => ({ ...event, userId: deviceId, isIdentified: false }),
  };
};

/** Single app-level owner of queue transport, retry, and lifecycle flushing. */
export const useMetricsEventsTransport = () => {
  const { authenticated, ready, userFriendlyDefiAddress } = useHubUser();
  const apiHelper = useApi();
  const isIdentified = Boolean(userFriendlyDefiAddress);
  const hasReportedIdentityStall = useRef(false);
  const previousIdentityKey = useRef<string | null>(null);
  /**
   * Identity needs the HubUser profile and nothing else: `userFriendlyDefiAddress`
   * derives from `hubUser.defiAddress`, which the wallet-connection handshake
   * (wagmi reconnect, Privy wallet readiness, address match) cannot change. Gating
   * on that handshake only widens the window in which a hidden tab loses its queue.
   */
  const identityResolving = !ready || (authenticated && !isIdentified);

  // Derived from state, not from the logout callback: a session also ends via token
  // expiry or an external wallet-account switch, neither of which calls logout().
  useEffect(() => {
    if (identityResolving) return;
    const identityKey = userFriendlyDefiAddress ? `identified:${userFriendlyDefiAddress}` : null;
    const previous = previousIdentityKey.current;
    previousIdentityKey.current = identityKey;
    // Only an identified session leaving: the anonymous handshake queue is kept on purpose.
    if (previous && previous !== identityKey) clearQueuedMetricsEvents();
  }, [identityResolving, userFriendlyDefiAddress]);

  const flushDeps: MetricsFlushDeps = useMemo(
    () => ({
      send: async (events: SendEventDtoType[], options?: { keepalive?: boolean }) => {
        const response = await apiHelper.postOrThrow(API_ENDPOINT.HUB_SEND_EVENTS_BULK, {
          bodyParams: { events },
          ...options,
        });
        // The route answers 200 with `success: false` for its own failures.
        if (response?.success === false) throw new Error("hub metrics bulk send rejected by the API");
      },
      resolveIdentity: (): MetricsIdentityStamp | null => {
        if (identityResolving) return null;

        // Read here, not at render: the device id only exists once consent starts Mixpanel.
        const userId = userFriendlyDefiAddress || mixpanelClient.getDistinctId() || null;
        if (!userId) {
          if (!hasReportedIdentityStall.current) {
            hasReportedIdentityStall.current = true;
            Sentry.captureMessage("mixpanel identity unavailable at flush — retaining queued events", {
              extra: { queuedCount: metricsEventsQueue.pendingCount },
            });
          }
          return null;
        }

        hasReportedIdentityStall.current = false;
        return {
          key: `${isIdentified ? "identified" : "anonymous"}:${userId}`,
          stamp: (event) => ({ ...event, userId, isIdentified }),
        };
      },
      resolveAnonymousIdentity,
      isPermanentFailure: isPermanentSendFailure,
      // Only what someone can act on reaches Sentry. A closing page losing its
      // connection and a queue isolating an ended session are outcomes, not faults, and
      // an issue nobody can close buries the ones that need a person.
      onUndelivered: (info) => {
        if (!ACTIONABLE_UNDELIVERED_REASONS.has(info.reason)) return;

        Sentry.captureMessage(`metrics events undelivered: ${info.reason}`, {
          tags: {
            metricsUndeliveredReason: info.reason,
            ...(info.trigger ? { metricsUnloadTrigger: info.trigger } : {}),
            ...(info.cause ? { metricsSendFailureCause: info.cause } : {}),
          },
          extra: info,
        });
      },
    }),
    [apiHelper, identityResolving, isIdentified, userFriendlyDefiAddress],
  );

  const { mutate: processEventQueue } = useMutation({
    retry: FLUSH_RETRY_COUNT,
    retryDelay: FLUSH_RETRY_DELAY_MS,
    mutationFn: () => flushMetricsQueue(metricsEventsQueue, flushDeps),
    onError: (error) => Sentry.captureException(error),
  });

  useEffect(() => {
    if (identityResolving) return;
    processEventQueue();
  }, [identityResolving, processEventQueue]);

  useEffect(
    () =>
      metricsEventsQueue.subscribe(() => {
        if (!identityResolving) processEventQueue();
      }),
    [identityResolving, processEventQueue],
  );

  useEffect(
    () => attachMetricsUnloadFlush(metricsEventsQueue, () => (analyticsAllowed() ? flushDeps : null)),
    [flushDeps],
  );
};

/** Queues backend-proxied Mixpanel events for the app-level transport. */
export const useMetricsEvents = () => {
  const trackEvent = useCallback((event: MetricsEvents, properties: Record<string, Any>): boolean => {
    if (!analyticsAllowed()) return false;

    metricsEventsQueue.enqueue(buildQueuedEvent(event, properties));
    return true;
  }, []);

  return { trackEvent };
};

type UseTrackEventProps = {
  event: MetricsEvents;
  properties?: Record<string, Any> | (() => Record<string, Any>);
  enabled?: boolean;
};

/** Tracks a metrics event once when the component becomes enabled. */
export const useTrackEvent = ({ event, properties = {}, enabled = true }: UseTrackEventProps) => {
  const { trackEvent } = useMetricsEvents();
  const hasBeenFired = useRef(false);
  const analyticsAgreed = useAnalyticsAllowed();

  useEffect(() => {
    // Latch on the send, not the attempt: a page view fired while the banner is still
    // open must fire again once the visitor accepts.
    if (hasBeenFired.current || !enabled || !analyticsAgreed) return;
    const rawProps = typeof properties === "function" ? properties() : properties;
    const props =
      PAGE_VIEW_EVENTS.has(event) && rawProps.previousPage == null
        ? { ...rawProps, previousPage: getPreviousPage() }
        : rawProps;

    hasBeenFired.current = trackEvent(event, props);
  }, [event, enabled, properties, trackEvent, analyticsAgreed]);
};
